import moment from 'moment';
import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createContext } from 'react';

import { CurrencyTypeEnum } from '../../../model/currency_type.enum';
import { DataLabelEnum } from '../../../model/data_label.enum';
import { SnapshotTypeEnum } from '../../../model/snapshot_type';

import { GalleryContext } from '../../gallery-provider';
import { GetVolContext } from '../../get-vol-provider';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import { showToast } from '../../../components/toast-notification';
import { lineChartColors } from '../../../theme/colors';
import usePersistentState from '../../use-persistent-state';

export interface VolCompareChips {
  key: string;
  color: string;
  visible: boolean;
  disabled?: boolean;
  data?: any;
  isRemovible?: boolean;
}

interface IVolCompareProvider {
  dataExpiry: any;
  setDataExpiry: React.Dispatch<React.SetStateAction<any>>;
  dataTenor: any;
  setDataTenor: React.Dispatch<React.SetStateAction<any>>;
  snapshotType: SnapshotTypeEnum;
  setSnapshotType: (snapshotType: SnapshotTypeEnum) => void;
  timestamp: string;
  getData: (timestamp: string, requestKey: string) => void;
  tenor: string;
  setTenor: (tenor: string) => void;
  tenors: string[];
  setTenors: (tenors: string[]) => void;
  expiry: string;
  setExpiry: (expiry: string) => void;
  expiries: string[];
  setExpiries: (expiries: string[]) => void;
  token: CurrencyTypeEnum;
  setToken: (token: CurrencyTypeEnum) => void;
  chips: VolCompareChips[];
  setChips: React.Dispatch<React.SetStateAction<VolCompareChips[]>>;
  hasDefaultToken: boolean;
}

interface IVolCompareParamsProvider {
  children?: ReactNode;
  providerKey?: string;
  isTermStructure?: boolean | false;
  currency?: CurrencyTypeEnum;
  label?: DataLabelEnum;
}

const DEFAULT_CHIPS = ['live-data', 'T-1', 'T-7', 'T-30', 'T-90', 'T-180'];

export const VolCompareContext = createContext({} as IVolCompareProvider);

export const VolCompareProvider = (props: IVolCompareParamsProvider) => {
  const { children, isTermStructure, providerKey } = props;

  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const galleryContext = useContext(GalleryContext);
  const { data: dataLive, handleResetSubscription } = useContext(GetVolContext);

  const label = props?.label || galleryContext?.label;

  const generateKey = (baseKey: string) => (providerKey ? `${baseKey}_${providerKey}` : baseKey);

  const KEY_VOL_SMILE = generateKey('GET_VOL_COMPARE');
  const KEY_TERMS = generateKey('GET_VOL_COMPARE_TERMS');
  const LIVE_DATA_KEY = generateKey('live-data');

  const KEY = isTermStructure ? KEY_TERMS : KEY_VOL_SMILE;

  const persistentKey = providerKey || 'default';
  const STATE_PATH = ['VOL_COMPARE', persistentKey];

  const [chips, setChips] = usePersistentState<VolCompareChips[]>(STATE_PATH, 'chips', []);
  const [dataExpiry, setDataExpiry] = useState(null);
  const [dataTenor, setDataTenor] = useState(null);
  const [timestamp, setTimestamp] = useState(moment().utc().toISOString());
  const [expiry, setExpiry] = usePersistentState<string>(STATE_PATH, 'expiry', '');
  const [expiries, setExpiries] = useState<string[]>([]);
  const [tenor, setTenor] = usePersistentState<string>(STATE_PATH, 'tenor', '1W');
  const [tenors, setTenors] = useState<string[]>([]);
  const [snapshotType, setSnapshotType] = usePersistentState<SnapshotTypeEnum>(
    STATE_PATH,
    'snapshotType',
    isTermStructure ? SnapshotTypeEnum.CM : SnapshotTypeEnum.EE,
  );

  const { currency: token, setCurrency: setToken, hasDefaultToken } = useContext(GetVolContext);

  const volDataLive = dataLive[snapshotType];

  const inFlightRef = useRef<Set<string>>(new Set());
  const subscribedRef = useRef<Set<string>>(new Set());
  const latestHandleDataRef = useRef<(d: any, requestKey?: string) => void>(() => {});

  const handleData = useCallback(
    (data: any, requestKey?: string) => {
      if (!data) return;

      const { timestamp, tenors } = data;
      if (!timestamp) return;

      const onlyDate = timestamp.substring(0, 10);
      if (!tenors || tenors.length === 0) return;

      if (snapshotType === SnapshotTypeEnum.EE) {
        setDataExpiry((prev: any) => ({
          ...prev,
          [onlyDate]: tenors,
        }));
      } else {
        setDataTenor((prev: any) => ({
          ...prev,
          [onlyDate]: tenors,
        }));
      }

      if (
        !isTermStructure &&
        snapshotType === SnapshotTypeEnum.EE &&
        requestKey === LIVE_DATA_KEY
      ) {
        const expiryKeys = Object.keys(tenors);

        setExpiries(expiryKeys);

        const currentExpiry = Array.isArray(expiry) ? expiry[0] : expiry;

        if (!expiryKeys.includes(currentExpiry)) {
          setExpiry(expiryKeys[0]);
        }
      }

      if (!isTermStructure && snapshotType === SnapshotTypeEnum.CM) {
        const has1m = !!Object.keys(tenors).includes('1M');
        if (!Object.keys(tenors).includes(tenor)) {
          if (has1m) {
            setTenor('1M');
          } else {
            setTenor(Object.keys(tenors)[0]);
          }
        }
        setTenors(Object.keys(tenors));
      }

      if (isTermStructure) {
        const firstTenor = Object.values(tenors)[0] as any;
        const tenorsKeys = Object.keys(firstTenor);
        const currentTenor = Array.isArray(tenor) ? tenor[0] : tenor;

        if (!tenorsKeys.includes(currentTenor)) {
          setTenor('ATMF');
        }
        const priorityOrder = [
          '10P',
          '20P',
          '25P',
          '40P',
          'ATMF',
          '40C',
          '25C',
          '20C',
          '10C',
          '5C',
        ];
        const orderedTenors = [
          ...priorityOrder.filter((k) => tenorsKeys.includes(k)),
          ...tenorsKeys.filter((k) => !priorityOrder.includes(k)),
        ].filter((t) => !['Future', 'DF', 'T'].includes(t));
        setTenors(orderedTenors);
      }

      const serverTime = moment(timestamp).utc();
      const isToday = moment().isSame(serverTime, 'day');
      if (isToday) {
        setTimestamp(timestamp);
      }
    },
    [
      isTermStructure,
      snapshotType,
      expiry,
      tenor,
      setDataExpiry,
      setDataTenor,
      setExpiries,
      setExpiry,
      setTenor,
      setTenors,
      setTimestamp,
    ],
  );

  useEffect(() => {
    latestHandleDataRef.current = handleData;
  }, [handleData]);

  const handleHistoricData = (response: any) => {
    if (!(!!response?.id && response.id.startsWith(KEY))) return;

    handleData(response.params ? response.params.data : response.result);
  };

  useEffect(() => {
    subscribeToChannel(KEY, handleHistoricData);
  }, []);

  const getData = useCallback(
    (timestamp: string, requestKey: string) => {
      const channel = `${KEY}_${requestKey}`;

      if (inFlightRef.current.has(channel)) return;
      inFlightRef.current.add(channel);

      const request = {
        id: channel,
        method: 'historic_vol_surface',
        params: {
          type: snapshotType,
          model: 'SVI',
          token: token || CurrencyTypeEnum.BITCOIN,
          format: 'json',
          timestamp: timestamp,
        },
        jsonrpc: '2.0',
      };

      sendMessage(JSON.stringify(request));

      if (!subscribedRef.current.has(channel)) {
        subscribeToChannel(channel, (res: any) => {
          inFlightRef.current.delete(channel);

          if (!res || res?.error) {
            if (!requestKey.includes('T-') && requestKey !== LIVE_DATA_KEY) {
              setChips((prev) => prev.filter((item) => item.key !== requestKey));
              showToast({
                message: 'Data loading error',
                type: 'error',
              });
            }
            return;
          }

          latestHandleDataRef.current(res?.result, requestKey);
        });
        subscribedRef.current.add(channel);
      }
    },
    [KEY, token, snapshotType, sendMessage, subscribeToChannel, setChips],
  );

  const resetData = useCallback(() => {
    setDataExpiry(null);
    setDataTenor(null);

    setChips((prevChips) => {
      if (prevChips.length) {
        return prevChips;
      }

      return DEFAULT_CHIPS.map((chip: any, index: number) => ({
        key: chip,
        color: lineChartColors[index],
        visible: true,
        disabled: false,
      }));
    });
  }, [chips, dataExpiry, dataTenor]);

  useEffect(() => {
    if (volDataLive && volDataLive?.params && volDataLive.params?.data) {
      handleData(volDataLive.params.data, LIVE_DATA_KEY);
    }
  }, [volDataLive]);

  useEffect(() => {
    resetData();

    if (volDataLive && volDataLive?.params && volDataLive.params?.data) {
      handleData(volDataLive.params.data, LIVE_DATA_KEY);
    }
  }, [label, snapshotType]);

  useEffect(() => {
    resetData();

    handleResetSubscription(snapshotType, token);
  }, [token]);

  useEffect(() => {
    inFlightRef.current.clear();
  }, [token, label, snapshotType]);

  return (
    <VolCompareContext.Provider
      value={{
        dataExpiry,
        setDataExpiry,
        dataTenor,
        setDataTenor,
        snapshotType,
        setSnapshotType,
        timestamp,
        getData,
        tenor,
        setTenor,
        tenors,
        setTenors,
        expiry,
        setExpiry,
        expiries,
        setExpiries,
        token,
        setToken,
        chips,
        setChips,
        hasDefaultToken,
      }}
    >
      {children}
    </VolCompareContext.Provider>
  );
};
